import { useQuery } from '@tanstack/react-query';
import { discoveryApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { QUERY_KEYS } from '../../queryKeys';
import { useState } from 'react';
import { useDebounce } from 'react-use';
import { ConfigurationItem } from './ConfigurationItemPickerExtension';

export const useConfigurationItems = (searchText: string, minimumLengthForSearch: number) => {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);

  const DEBOUNCE_DELAY_MS = 500;

  useDebounce(
    () => {
      setDebouncedSearchText(searchText);
    },
    DEBOUNCE_DELAY_MS,
    [searchText],
  );

  const getConfigurationItems = async (): Promise<ConfigurationItem[]> => {
    const baseUrl = await discoveryApi.getBaseUrl('servicenow');
    const { token } = await identityApi.getCredentials();
    const url = new URL(`${baseUrl}/applications`);
    url.searchParams.set('searchText', debouncedSearchText);
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch configuration items: ${response.statusText}`);
    }
    const data = await response.json();
    return data.applications;
  };

  const {
    data: configurationItems,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.scaffolder.configurationItems(debouncedSearchText),
    queryFn: getConfigurationItems,
    enabled: debouncedSearchText.length >= minimumLengthForSearch,
  });

  return { configurationItems, isLoading, error };
};
